import { isPlatformBrowser } from '@angular/common';
import { inject, PLATFORM_ID, RendererFactory2, DOCUMENT } from '@angular/core';
import { AccessibilityModule } from './accessibility-module.interfaces';
import { AccessibilityCssVarsHelper } from './css-vars.helper';
import { AccessibilityBlueFilterHandler } from './handlers/blue-filter.handler';
import { AccessibilityColorBlindnessFilterHandler } from './handlers/color-blindness-filter.handler';
import { AccessibilityDarkModeHandler } from './handlers/dark-mode.handler';
import * as i0 from "@angular/core";
export let AccessibilityHandlerService = /*#__PURE__*/(() => {
  class AccessibilityHandlerService {
    constructor() {
      this.isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
      this.doc = inject(DOCUMENT);
      this.renderer = inject(RendererFactory2).createRenderer(null, null);
      this.cssVarsHelper = inject(AccessibilityCssVarsHelper);
      this.handlers = {
        [AccessibilityModule.Zoom]: (enabled, config) => {
          if (!enabled) {
            this.cssVarsHelper.removeCssVarsOverride(['--fontSize']);
          } else {
            this.cssVarsHelper.overrideCssVars({
              '--fontSize': config.value + 'em'
            });
          }
        },
        [AccessibilityModule.DarkMode]: inject(AccessibilityDarkModeHandler),
        [AccessibilityModule.SimpleFont]: (enabled, config) => {
          if (!enabled || !config.enabled) {
            this.cssVarsHelper.removeCssVarsOverride(['--fontFamily', '--fontLetterSpacing', '--headlinesFontFamily']);
          } else {
            this.cssVarsHelper.overrideCssVars({
              '--fontFamily': 'Helvetica, "Open Sans", Arial, sans-serif',
              '--fontLetterSpacing': '0.04rem',
              '--headlinesFontFamily': 'Helvetica, "Open Sans", Arial, sans-serif'
            });
          }
        },
        [AccessibilityModule.HighContrast]: (enabled, config) => {},
        [AccessibilityModule.BlueFilter]: inject(AccessibilityBlueFilterHandler),
        [AccessibilityModule.ColorBlindnessFilter]: inject(AccessibilityColorBlindnessFilterHandler),
        [AccessibilityModule.ContrastAndSaturation]: (enabled, config) => {
          if (!enabled) {
            this.renderer.removeStyle(this.doc.body, 'filter');
          } else {
            let styleValue = '';
            if (typeof config.contrast === 'number' && config.contrast !== 1.0) styleValue += 'contrast(' + config.contrast + ') ';
            if (typeof config.saturation === 'number' && config.saturation !== 1.0) styleValue += 'saturate(' + config.saturation + ') ';
            this.renderer.setStyle(this.doc.body, 'filter', styleValue.length > 0 ? styleValue : 'none');
          }
        },
        [AccessibilityModule.LowDistractionMode]: (enabled, config) => {
          if (!enabled || !config.enabled) {
            this.renderer.removeClass(this.doc.body, 'a11y-no-distraction-mode');
          } else {
            this.renderer.addClass(this.doc.body, 'a11y-no-distraction-mode');
          }
        },
        [AccessibilityModule.MarkInteractibleElements]: (enabled, config) => {
          if (!enabled || !config.enabled) {
            this.renderer.removeClass(this.doc.body, 'a11y-highlight-interactible');
          } else {
            this.renderer.addClass(this.doc.body, 'a11y-highlight-interactible');
          }
        },
        [AccessibilityModule.EmphasizeFocusedElement]: (enabled, config) => {
          if (!enabled || !config.enabled) {
            this.renderer.removeClass(this.doc.body, 'a11y-emphasize-focus');
          } else {
            this.renderer.addClass(this.doc.body, 'a11y-emphasize-focus');
          }
        },
        [AccessibilityModule.CursorSize]: (enabled, config) => {}
      };
      if (this.isBrowser) {
        if (window?.matchMedia) {
          const prefersColorThemeChangeHandler = matches => {};
          const preferDarkTheme = window.matchMedia("(prefers-color-scheme: dark)");
          preferDarkTheme.addEventListener("change", e => prefersColorThemeChangeHandler(e.matches));
          prefersColorThemeChangeHandler(preferDarkTheme.matches);
        }
      }
    }
    applyModules(list, haveModulesOnlyBeenUpdated) {
      // TODO: store previous list state and diff to only apply modifications
      list.forEach(entry => {
        const handler = this.handlers[entry.moduleId];
        if (typeof handler === 'object') {
          handler.onUpdate(entry.enabled, entry.config);
        } else {
          handler(entry.enabled, entry.config);
        }
      });
    }
    static {
      this.ɵfac = function AccessibilityHandlerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AccessibilityHandlerService)();
      };
    }
    static {
      this.ɵprov = /*@__PURE__*/i0.ɵɵdefineInjectable({
        token: AccessibilityHandlerService,
        factory: AccessibilityHandlerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AccessibilityHandlerService;
})();